import React from "react";
import { useSetting, useSettingsActions } from "./SettingsStore.tsx";
import { SelectButton } from "primereact/selectbutton";
import { RadioButton } from "primereact/radiobutton";
import { PYTHAGOREAN_MAX_SIDE_OPTIONS, PythagoreanQuestionOptions } from "../Constants.ts";
import { withLabels } from "../Labels.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";

/**
 * The two Pythagorean theorem dials (ADR 0109 §2).
 *
 * Longest side bounds the hypotenuse every question is built from, so it
 * applies to all four questions and is never hidden. Both are basic tier, as
 * the format's own panel is (ADR 0023 §2). Reads its own slice of the settings
 * (ADR 0081), so a change elsewhere on the panel does not redraw it.
 */
export const PythagoreanSettings = React.memo(function PythagoreanSettings() {
    const { getSettings, setSettings } = useSettingsActions();
    const { question, maxSide, language } = useSetting((settings) => ({
        question: settings.PythagoreanQuestion,
        maxSide: settings.PythagoreanMaxSide,
        language: settings.Language,
    }));
    const t = useTranslation();

    function handleQuestionChange(value: PythagoreanQuestionOptions) {
        // SelectButton hands back null when the selected option is clicked
        // again; a sheet has to ask something.
        if (!value) {
            return;
        }
        setSettings({ ...getSettings(), PythagoreanQuestion: value });
    }

    return (
        <div className="pythagoreanSettingsContainer">
            <div className="regroupingContainer">
                <SelectButton
                    value={question}
                    onChange={(e) => handleQuestionChange(e.value)}
                    options={withLabels(Object.values(PythagoreanQuestionOptions), language)}
                    className="select regrouping"
                    aria-label={t('settings.questionType')}
                />
            </div>
            <div className="selectContainer">
                <span className="settingLabel">{t('settings.longestSideLabel')}</span>
            </div>
            <div className="radioButtons" role="radiogroup" aria-label={t('settings.longestSideAria')}>
                {PYTHAGOREAN_MAX_SIDE_OPTIONS.map((option) => (
                    <div key={option}>
                        <RadioButton
                            inputId={`pythagoreanMaxSide-${option}`}
                            value={option}
                            checked={maxSide === option}
                            onChange={() => setSettings({ ...getSettings(), PythagoreanMaxSide: option })}
                        />
                        <label htmlFor={`pythagoreanMaxSide-${option}`}>{option}</label>
                    </div>
                ))}
            </div>
        </div>
    );
});

export default PythagoreanSettings;
